import {useState, useCallback} from 'react'
import {fetchToken} from '../api/tokenApi'

interface UseLiveKitRoomOptions {
  userId: string
}

type ConnectionState =
  | {status: 'idle'}
  | {status: 'connecting'}
  | {status: 'error'; error: string}
  | {status: 'connected'; token: string}

interface UseLiveKitRoomResult {
  connection: ConnectionState
  connect: (roomId: string, username: string) => Promise<void>
}

/**
 * Hook for managing LiveKit room connection.
 * Fetches token and provides connection state as a discriminated union.
 */
export function useLiveKitRoom({userId}: UseLiveKitRoomOptions): UseLiveKitRoomResult {
  const [connection, setConnection] = useState<ConnectionState>({
    status: 'idle',
  })

  const connect = useCallback(
    async (roomId: string, username: string) => {
      setConnection({status: 'connecting'})

      try {
        const response = await fetchToken({
          roomId,
          participantName: username,
          participantIdentity: userId,
        })

        setConnection({status: 'connected', token: response.token})
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to connect to room'
        setConnection({status: 'error', error: message})
        console.error('[useLiveKitRoom] Connection error:', err)
      }
    },
    [userId],
  )

  return {connection, connect}
}
